import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MainLayout from '../../layout/MainLayout';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { keyframes } from '@mui/system';

// Custom hooks and components
import useDataCatalogState from './data_catalog/hooks/useDataCatalogState';
import useFilterSort from './data_catalog/hooks/useFilterSort';
import useWorkingLists from './data_catalog/hooks/useWorkingLists';
import DataCatalogTable from './data_catalog/components/DataCatalogTable';
import CompactFilterBar from './data_catalog/components/CompactFilterBar';
import FilterExpansionPanel from './data_catalog/components/FilterExpansionPanel';
import WorkingListDialog from './data_catalog/components/WorkingListDialog';
import ListAddRequestDialog from './data_catalog/components/ListAddRequestDialog';
import ListManagementDialog from './data_catalog/components/ListManagementDialog';
import AIAssistantDialog from './data_catalog/components/AIAssistantDialog';
import TagManagementDialog from './data_catalog/components/TagManagementDialog';
import SetManagementDialog from './data_catalog/components/SetManagementDialog';
import TagDeletionRequestDialog from './data_catalog/components/TagDeletionRequestDialog';
import { OmopMappingBatchStartDialog } from '../../components/olymp/OmopMappingActions';
import { getPrimaryName as getPrimaryNameAusHelfer, getSecondaryName as getSecondaryNameAusHelfer, tagIconOptions as importedTagIconOptions, getIconComponent as importedGetIconComponent, erzwungenerStatusfilter, zeigtOpenEhr } from './data_catalog/utils/helpers';
import ArchetypChips from './data_catalog/components/ArchetypChips';
import { oeffneListenDialogFuerParameter } from './data_catalog/utils/listDialogHandlers';
import DataCatalogHelpSection from '../../components/olymp/DataCatalogHelpSection';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/authContext';

// Catalog API service
import {
  getParameters,
  getParameterPage,
  getParametersByIds,
  getCatalogTags,
  getCatalogStations,
  getCatalogSourceSystems,
  getCatalogArchetypes,
  getCatalogStatusCounts,
  getSavedSets,
  resolveFavoritesList,
  FAVORITES_LIST_NAME,
} from '../../services/catalogService';
import KatalogBlaetterung from './data_catalog/components/KatalogBlaetterung';
import {
  SEITENGROESSE_VORGABE,
  MAX_IDS_FILTER,
  apiOrdering,
  brauchtVollsatz,
  idsFuerFilter,
  idsFuerListenvergleich,
  ergebnisSchluessel,
  letzteSeite,
} from './data_catalog/katalogBlaetterung';

// Subtle pulse animation for tags with deletion requests
const subtlePulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(237, 108, 2, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(237, 108, 2, 0.1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(237, 108, 2, 0);
  }
`;

// Use imported versions from helpers.js
const tagIconOptions = importedTagIconOptions;
const getIconComponent = importedGetIconComponent;

function resolveCatalogStatusMessage(t, ...errors) {
  return errors.some(error => error?.status === 401)
    ? t('dataCatalog.status.authRequired')
    : t('dataCatalog.status.apiUnavailable');
}

const DataCatalog = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // #311: die Mindestbreiten der Arbeitslisten-Zeile (Beschriftung 180px +
  // Auswahl 250px + gap) gelten erst ab `md` -- unterhalb davon bricht die
  // Zeile um, statt die ganze Seite ueber die Fensterbreite hinaus zu
  // schieben. `useMediaQuery` statt eines `sx`-Breakpoint-Objekts (dasselbe
  // Muster wie MainLayout.js, #310): ein `sx`-Objekt wie `{ xs: 0, md: 180 }`
  // kompiliert zu einer `@media`-Regel, die jsdom fuer `getComputedStyle`
  // nicht auswertet -- der aufgeloeste JS-Wert hier schon (siehe MR, Abschnitt
  // Annahmen).
  const theme = useTheme();
  const istSchmalesFenster = useMediaQuery(theme.breakpoints.down('md'));

  const currentUser = user?.name || user?.email || '';
  const [overviewDialogOpen, setOverviewDialogOpen] = useState(false);
  const [mappingBatchDialogOpen, setMappingBatchDialogOpen] = useState(false);
  const [selectionHydrated, setSelectionHydrated] = useState(false);

  // Use custom hooks for state management
  const state = useDataCatalogState();
  const workingLists = useWorkingLists();

  // Destructure state for easier access
  const {
    searchTerm, setSearchTerm,
    nameView, setNameView,
    showFilters, setShowFilters,
    expandedRows, setExpandedRows,
    selectedTags, setSelectedTags,
    selectedStations, setSelectedStations,
    selectedSensitivity, setSelectedSensitivity,
    selectedOmopStatus, setSelectedOmopStatus,
    selectedOpenehrStatus, setSelectedOpenehrStatus,
    selectedSourceSystems, setSelectedSourceSystems,
    selectedArchetype, setSelectedArchetype, handleArchetypeToggle,
    onlyUnvalidatedAiSuggestions, setOnlyUnvalidatedAiSuggestions,
    filterPanelOpen, setFilterPanelOpen,
    orderBy, setOrderBy,
    order, setOrder,
    tagDialogOpen, setTagDialogOpen,
    editingParameterId, setEditingParameterId,
    newTagInput, setNewTagInput,
    listDialogOpen, setListDialogOpen,
    selectedParameterForList, setSelectedParameterForList,
    listManagementDialogOpen, setListManagementDialogOpen,
    setManagementDialogOpen, setSetManagementDialogOpen,
    activeSetTab, setActiveSetTab,
    createTagPrivate, setCreateTagPrivate,
    selectedTagIcon, setSelectedTagIcon,
    deletionRequestDialog, setDeletionRequestDialog,
    activeWorkingList, setActiveWorkingList,
    showOnlyActiveListParams, setShowOnlyActiveListParams,
    listComparisonFilter, setListComparisonFilter,
    listAddRequestDialog, setListAddRequestDialog,
    aiAssistantOpen, setAiAssistantOpen,
    studyDescription, setStudyDescription,
    studyType, setStudyType,
    specialty, setSpecialty,
    aiSuggestions, setAiSuggestions,
    selectedSuggestions, setSelectedSuggestions,
    isGeneratingSuggestions,
    aiSuggestedParameterIds, setAiSuggestedParameterIds,
    setParameterIds, setSetParameterIds,
    snackbar, setSnackbar,
    handleRowClick,
    isRowExpanded,
    handleTagToggle,
    handleStationToggle,
    handleSourceSystemToggle,
    clearAllFilters,
  } = state;

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  // Destructure working list functions
  const {
    parameterLists,
    error: workingListsError,
    selectionLoading,
    workingListSelection,
    persistWorkingListSelection,
    isParameterInAnyList,
    isParameterInActiveList,
    hasParameterPendingInActiveList,
    isParameterInList,
    hasParameterPendingApproval,
    handleAddToList: addToList,
    handleRemoveFromList: removeFromList,
    handleToggleParameterInList,
    createList,
    renameList,
    deleteList,
    duplicateList,
  } = workingLists;

  // Haelt die laufende Anlage der Favoriten-Liste fest (#255, Runde 2): ohne
  // diese Bremse legt ein Doppelklick vor der ersten API-Antwort zwei private
  // "Favoriten"-Listen an, weil `favoritesList` bis dahin noch `null` ist.
  const creatingFavoritesListRef = useRef(null);

  // ── API-driven data state ───────────────────────────────────────────────
  const [catalogParameters, setCatalogParameters] = useState([]);
  const [catalogDataSource, setCatalogDataSource] = useState('idle');
  const [catalogLoading, setCatalogLoading] = useState(true);
  // Laeuft der Vollsatz-Abruf noch, nachdem die erste Seite gezeigt wurde?
  // `catalogLoading` beantwortet das nicht: es faellt mit der ersten Seite
  // (AK3), waehrend ~9 weitere unterwegs sind (#191, Review Runde 3).
  const [catalogNachladen, setCatalogNachladen] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [savedSets, setSavedSets] = useState([]);
  // Das Schliessen des Erfolgs-Hinweises (Zeile ~910) durfte bisher nicht ueber
  // `catalogDataSource` laufen (Review Runde 2, Blocker 1): der Klick setzte
  // `catalogDataSource` auf `'acknowledged'`, und genau dieser Wert ist es, an
  // dem `gesamtzahl` (Zeile ~464) im Vollsatz-Modus "Abruf geglueckt" erkennt.
  // Nach dem Schliessen fiel `gesamtzahl` also auf das dort immer `null`
  // stehende `catalogCount` zurueck -- rund 1900 bereits geladene Zeilen wurden
  // fuer die Blaetterung und die Kopfzeile unerreichbar, obwohl an den Daten
  // nichts falsch war. Das Schliessen ist reine Anzeige-Praeferenz und bekommt
  // darum einen eigenen Zustand, unabhaengig von dem, der den Abrufserfolg traegt.
  const [erfolgshinweisAusgeblendet, setErfolgshinweisAusgeblendet] = useState(false);

  const [apiTags, setApiTags] = useState([]);
  const [apiStations, setApiStations] = useState([]);
  const [apiSourceSystems, setApiSourceSystems] = useState([]);
  // Archetyp-Chips (#788): der Vollbestand (unabhaengig von Suche/Tags/...,
  // nur die erzwungene Stellungs-Einschraenkung gilt) und die Zaehlung unter
  // den aktuell aktiven Filtern getrennt -- sonst verschwaende ein Archetyp,
  // dessen gefilterte Zaehlung gerade 0 ist, aus der Zeile (AK5).
  const [archetypUniverse, setArchetypUniverse] = useState([]);
  const [archetypCounts, setArchetypCounts] = useState([]);
  // Statusverteilung fuer die Filterleiste (#782 AK3c): `null` heisst "diese
  // Instanz kennt die Aufschluesselung nicht" (#783 liefert sie), nicht "0
  // Treffer je Status" -- siehe getCatalogStatusCounts.
  const [omopStatusCounts, setOmopStatusCounts] = useState(null);
  const [openehrStatusCounts, setOpenehrStatusCounts] = useState(null);

  // ── Blaetterung (argus-platform #193) ───────────────────────────────────────
  //
  // `catalogCount` ist die Gesamtzahl **aus der Antwort**, nicht die Laenge der
  // geladenen Liste. Genau das ist der Kern der Umstellung: die Anzeige
  // "n Parameter gefunden" bleibt richtig, obwohl nur eine Seite geladen ist.
  //
  // `null` heisst "unbekannt" und nicht "null Treffer". Die Unterscheidung ist der
  // Grund, warum eine leere Tabelle nicht mehr wie ein leerer Standort aussieht
  // (#191).
  const [seite, setSeite] = useState(1);
  const [seitengroesse, setSeitengroesse] = useState(SEITENGROESSE_VORGABE);
  const [catalogCount, setCatalogCount] = useState(null);
  // Die Gesamtzahl aller Zeilen, die die aktuellen Filter treffen -- ohne die
  // Einschraenkung auf eine der beiden Arbeitslisten (#277 Runde 2, B4). Nur
  // im Listenvergleich-ids-Modus gebraucht, um "neither" (Gesamtzahl minus
  // Vereinigung) als Zahl auf dem Chip zu zeigen, ohne dafuer den Vollsatz zu
  // laden. `null` heisst "noch nicht bekannt", nicht "null Treffer" (#191).
  const [listComparisonTotalCount, setListComparisonTotalCount] = useState(null);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  // Der Archetyp-Chip existiert nur in den Stellungen openEHR und "alle"
  // (AK4) -- die Zeile, die ihn bedienbar macht, blendet sich in den anderen
  // beiden Stellungen aus. Ohne diesen Rueckstellwert bliebe eine zuvor
  // getroffene Auswahl in `selectedArchetype` bestehen: ein unsichtbarer
  // Filter ohne Bedienelement, den weder die Chipzeile noch (vor dieser
  // Aenderung) die Filterleiste anzeigte (Review Runde 3, Blocker 2).
  useEffect(() => {
    if (!zeigtOpenEhr(nameView) && selectedArchetype !== null) {
      setSelectedArchetype(null);
    }
  }, [nameView, selectedArchetype, setSelectedArchetype]);

  useEffect(() => {
    let cancelled = false;

    async function loadSavedSets() {
      const result = await getSavedSets();
      if (cancelled) return;

      if (result.source === 'api') {
        setSavedSets(result.sets);
      } else {
        setSavedSets([]);
      }
    }

    loadSavedSets();

    return () => { cancelled = true; };
  }, []);

  // Was die API sortieren kann, sortiert die API -- sonst sortiert die Seite nach
  // der Umstellung nur die sichtbaren 50 (argus-platform #192).
  const ordering = apiOrdering(orderBy, order);

  // Die IDs der aktiven Arbeitsliste, wenn genau eine aktiv ist -- Grundlage
  // fuer "nur Parameter der aktiven Liste" als `ids=`-Filter (#277).
  const activeListParameterIds = activeWorkingList.length === 1
    ? (parameterLists.find(list => list.id === activeWorkingList[0])?.parameterIds || [])
    : [];

  // Zwei aktive Arbeitslisten: Vergleichsmodus (#277 Runde 2, B4). Die IDs
  // sind die Vereinigung beider Listen -- Grundlage fuer die Chips "nur A",
  // "nur B" und "beide". "alle" und "neither" gehen ueber `brauchtVollsatz`
  // (Runde 3, NEU-B5): "alle" ist keine Teilmenge der Vereinigung, siehe
  // idsFuerListenvergleich, katalogBlaetterung.js.
  const listeVergleichAktiv = activeWorkingList.length === 2;
  const listComparisonList1 = listeVergleichAktiv
    ? parameterLists.find(list => list.id === activeWorkingList[0])
    : null;
  const listComparisonList2 = listeVergleichAktiv
    ? parameterLists.find(list => list.id === activeWorkingList[1])
    : null;

  // Welche IDs statt eines Vollsatzes reichen -- oder `null`, wenn keiner der
  // Faelle aktiv ist (#277). Der Listenvergleich geht vor: idsFuerFilter kennt
  // den Fall zweier aktiver Listen nicht (siehe dort).
  const idsFilter = listeVergleichAktiv
    ? idsFuerListenvergleich({
      list1ParameterIds: listComparisonList1?.parameterIds || [],
      list2ParameterIds: listComparisonList2?.parameterIds || [],
    })
    : idsFuerFilter({
      aiSuggestedParameterIds,
      setParameterIds,
      showOnlyActiveListParams,
      activeListParameterIds,
    });

  // Oberhalb der Backend-Obergrenze faellt die Seite sichtbar auf den
  // Vollsatz-Weg zurueck (AK3), statt eine Anfrage zu schicken, die dort mit
  // 414 enden koennte: der Vollsatz-Weg holt dieselbe Menge ohnehin, nur
  // seitenweise statt in einer Anfrage.
  const idsUeberLimit = idsFilter !== null && idsFilter.length > MAX_IDS_FILTER;

  // Wann die Seite trotzdem den vollen Satz braucht: Begruendung und Liste der
  // Faelle stehen in data_catalog/katalogBlaetterung.js.
  const vollsatzModus = brauchtVollsatz({
    activeWorkingList,
    orderBy,
    onlyUnvalidatedAiSuggestions,
    listComparisonFilter,
    selectedArchetype,
  }) || idsUeberLimit;

  // Holt die Seite ihre Daten ueber einen `ids=`-Filter statt einer Seite
  // oder des Vollsatzes (#277)?
  const idsFilterModus = !vollsatzModus && idsFilter !== null;

  // Beide Modi haben die volle (gefilterte) Menge im Speicher und blaettern
  // im Browser -- der Unterschied liegt allein darin, wie sie dorthin kommen.
  const blaettertLokal = vollsatzModus || idsFilterModus;

  // Stabiler Schluessel fuer die Effekt-Abhaengigkeit: `idsFuerFilter` baut bei
  // jedem Rendern ein neues Array, auch bei gleichem Inhalt (dieselbe
  // Begruendung wie bei `ergebnisSchluessel` weiter unten).
  const idsFilterKey = idsFilter === null ? '' : JSON.stringify([...idsFilter].sort());

  // **Die Ansicht schraenkt die Menge ein, nicht nur die Spalten** (#782 AK3).
  // In Stellung OMOP bzw. openEHR zeigt die Liste nur, was dort zugeordnet
  // ist; in `lokal` und `alle` gilt keine Einschraenkung. Der erzwungene Wert
  // ueberschreibt einen von Hand gesetzten Statusfilter derselben Darstellung
  // -- sonst zeigte die Stellung "OMOP" mit Filter "nicht gemappt" eine leere
  // Liste, und niemand wuesste, welche der beiden Angaben gewonnen hat.
  const catalogFilters = useMemo(() => {
    const vonHand = {
      omopStatus: selectedOmopStatus && selectedOmopStatus !== 'all' ? selectedOmopStatus : '',
      openehrStatus:
        selectedOpenehrStatus && selectedOpenehrStatus !== 'all' ? selectedOpenehrStatus : '',
    };
    return {
      q: debouncedSearchTerm,
      tags: selectedTags,
      stations: selectedStations,
      sensitivity: selectedSensitivity && selectedSensitivity !== 'all' ? selectedSensitivity : '',
      sourceSystem: selectedSourceSystems,
      ...vonHand,
      ...erzwungenerStatusfilter(nameView),
    };
  }, [
    debouncedSearchTerm,
    nameView,
    selectedOmopStatus,
    selectedOpenehrStatus,
    selectedSensitivity,
    selectedSourceSystems,
    selectedStations,
    selectedTags,
  ]);

  // Statusverteilung je Achse (#782 AK3c): die Zaehlung fuer eine Achse laeuft
  // ohne deren **eigenen** Statusfilter (von Hand gesetzt oder erzwungen) --
  // sonst zeigte die Stellung "OMOP" (erzwingt `omopStatus=validated,suggested`)
  // fuer "nicht gemappt" immer 0, obwohl es die interessantere Zahl ist. Die
  // jeweils andere Achse bleibt stehen: in Stellung "alle" soll die Kombination
  // `openehr_status=validated & omop_status=not_mapped` (AK4) auch in den
  // gezeigten Zahlen sichtbar werden, nicht nur im Ergebnis.
  const statusCountsOmopFilters = useMemo(() => {
    const { omopStatus, ...rest } = catalogFilters;
    return rest;
  }, [catalogFilters]);
  const statusCountsOpenehrFilters = useMemo(() => {
    const { openehrStatus, ...rest } = catalogFilters;
    return rest;
  }, [catalogFilters]);

  // Eine andere Ergebnismenge heisst: zurueck auf Seite 1. Ohne das zeigt eine
  // Suche mit fuenf Treffern weiterhin "Seite 7" -- und damit nichts.
  const ergebnisKey = ergebnisSchluessel({
    ...catalogFilters,
    ordering,
    pageSize: seitengroesse,
    archetype: selectedArchetype,
  });
  useEffect(() => {
    setSeite(1);
  }, [ergebnisKey]);

  useEffect(() => {
    let cancelled = false;

    async function loadCatalogData() {
      setCatalogLoading(true);
      // Ein neuer Abruf faengt nicht im Nachlade-Zustand des vorigen an --
      // sonst bliebe das Skelett stehen, obwohl nichts mehr unterwegs ist.
      setCatalogNachladen(false);
      setCatalogError(null);
      // `catalogDataSource` bleibt sonst vom vorherigen Abruf stehen: laeuft der
      // Modus zwischen zwei Effekt-Durchlaeufen von Blaetterung auf Vollsatz um
      // (Hydrierung der Arbeitslisten-Auswahl), zeigt `onPage` (Zeile ~316) schon
      // die erste Seite, waehrend `catalogDataSource` noch das `'api'` des alten,
      // laengst abgeschlossenen Abrufs traegt -- `gesamtzahl` (Zeile ~452) liest
      // das als "Abruf geglueckt" und rechnet `filteredParameters.length` genau
      // dort vor, wo AK3 "unbekannt" verlangt.
      setCatalogDataSource('idle');
      setErfolgshinweisAusgeblendet(false);
      // `catalogCount` nur im Vollsatz-Modus zuruecksetzen (Review Runde 2,
      // Blocker 2): sonst traegt er beim Umschalten auf Vollsatz noch die Zahl
      // aus der letzten Blaetter-Antwort, und `gesamtzahl` (Zeile ~460) liest
      // eine stehengebliebene Zahl statt `null` ("unbekannt"), obwohl der neue
      // Abruf noch laeuft. Im Blaetterungs-Modus dagegen ersetzt die naechste
      // Antwort `catalogCount` ohnehin (Zeile ~360) -- ein Reset auf `null` bei
      // jedem Seitenwechsel liesse `KatalogBlaetterung` (die bei `gesamtzahl <=
      // 0` nichts rendert, siehe dort) zwischenzeitlich verschwinden und wieder
      // erscheinen: Fokusverlust und Layoutsprung bei jedem Klick auf "naechste
      // Seite", ohne dass sich an der Ergebnismenge etwas geaendert haette.
      if (vollsatzModus) {
        setCatalogCount(null);
      }

      // Eine Anfrage je Seitenansicht (#193) -- ausser die Seite braucht den
      // vollen Satz, dann bleibt es beim alten Weg ueber alle Seiten.
      //
      // `ordering` geht auch im Vollsatz-Modus mit: dort sortierte bisher
      // niemand: die API kannte den Parameter nicht (#192), und der lokale
      // Sortierer kennt nur die Listenspalten -- fuer alles andere gibt er die
      // Liste unveraendert zurueck. Ein Klick auf "Name" tat also nichts. Weil
      // die Sortierung der API total ist (`id` als Zweitschluessel), bleibt die
      // Reihenfolge ueber alle Seiten hinweg richtig.
      // Im Vollsatz-Modus haengt `getParameters` mehrere Anfragen zu je 200
      // Zeilen aneinander (Seitengroesse in catalogService.js): ohne `onPage`
      // bliebe die Tabelle bis zur letzten davon leer -- genau der Zustand,
      // gegen den #191 geschrieben ist, nur mit weniger Seiten als vor #193.
      // `onPage` zeigt die erste Seite, sobald sie da ist (AK3); `cancelled`
      // verhindert, dass eine Seite eines bereits verworfenen Abrufs
      // (Filterwechsel waehrend des Ladens) noch Zustand setzt.
      // idsFilterModus (#277): eine Anfrage mit `ids=` statt aller Seiten fuer
      // KI-Vorschlaege, ein geladenes Set oder "nur Parameter der aktiven
      // Liste". Eine leere ID-Liste ist ein gueltiges "null Treffer" -- keine
      // Anfrage mit leerem `ids`-Parameter, der von "kein Filter" nicht zu
      // unterscheiden waere und den ganzen Katalog zurueckgeben wuerde.
      const parameterAbruf = vollsatzModus
        ? getParameters({ ...catalogFilters, ordering }, {
          onPage: (seitenweise) => {
            if (cancelled) return;
            setCatalogParameters(seitenweise);
            setCatalogLoading(false);
            // Ab hier ist `catalogLoading` falsch, aber fertig ist nichts.
            setCatalogNachladen(true);
          },
        })
        : idsFilterModus
          ? (idsFilter.length === 0
            ? Promise.resolve({ parameters: [], count: 0, source: 'api' })
            : getParametersByIds(idsFilter, { ...catalogFilters, ordering }))
          : getParameterPage({
            filters: catalogFilters,
            page: seite,
            pageSize: seitengroesse,
            ordering,
          });

      // Die Gesamtzahl ohne Listen-Einschraenkung -- nur im Listenvergleich
      // gebraucht, und nur dort, wo die Seite nicht ohnehin den Vollsatz laedt
      // (#277 Runde 2, B4): im Vollsatz-Modus zaehlt useFilterSort "neither"
      // direkt aus der bereits geladenen vollen Menge (siehe getListComparisonCounts).
      const listComparisonTotalAbruf = (listeVergleichAktiv && idsFilterModus)
        ? getParameterPage({ filters: catalogFilters, page: 1, pageSize: 1 })
        : Promise.resolve(null);

      // Archetyp-Chips (#788), nur in den Stellungen, in denen sie erscheinen
      // (AK4). Zwei getrennte Abrufe: der Vollbestand (nur die erzwungene
      // Stellungs-Einschraenkung, sonst ungefiltert) nennt jeden Archetyp, den
      // es ueberhaupt gibt -- die Zaehlung unter `catalogFilters` sagt, wie
      // viele davon die *aktuell* aktiven Filter treffen (AK3). Ohne die
      // Trennung verschwaende ein Archetyp mit gefilterter Zaehlung 0 aus der
      // Zeile, statt eine 0 zu zeigen (AK5).
      const archetypUniverseAbruf = zeigtOpenEhr(nameView)
        ? getCatalogArchetypes(erzwungenerStatusfilter(nameView))
        : Promise.resolve(null);
      const archetypCountsAbruf = zeigtOpenEhr(nameView)
        ? getCatalogArchetypes(catalogFilters)
        : Promise.resolve(null);

      // Statusverteilung fuer die Filterleiste (#782 AK3c) -- beide Achsen
      // immer, unabhaengig von der Stellung: beide Statusfilter sind in jeder
      // Stellung bedienbar (CompactFilterBar), nicht nur in "alle".
      const omopStatusCountsAbruf = getCatalogStatusCounts(statusCountsOmopFilters);
      const openehrStatusCountsAbruf = getCatalogStatusCounts(statusCountsOpenehrFilters);

      const [
        paramResult, tagResult, stationResult, sourceSystemResult, listComparisonTotalResult,
        archetypUniverseResult, archetypCountsResult,
        omopStatusCountsResult, openehrStatusCountsResult,
      ] = await Promise.all([
        parameterAbruf,
        getCatalogTags(catalogFilters),
        getCatalogStations(catalogFilters),
        getCatalogSourceSystems(catalogFilters),
        listComparisonTotalAbruf,
        archetypUniverseAbruf,
        archetypCountsAbruf,
        omopStatusCountsAbruf,
        openehrStatusCountsAbruf,
      ]);

      if (cancelled) return;

      setCatalogParameters(paramResult.parameters);
      setCatalogDataSource(paramResult.source);
      // Im Vollsatz-Modus zaehlt die lokal gefilterte Menge, sonst die Antwort.
      // `null` ist hier "unbekannt": im Fehlerfall darf nirgends "0 Parameter"
      // stehen, denn das ist eine Aussage, die die Seite nicht treffen kann (#191).
      setCatalogCount(vollsatzModus ? null : paramResult.count);
      setListComparisonTotalCount(
        listComparisonTotalResult && listComparisonTotalResult.source === 'api'
          ? listComparisonTotalResult.count
          : null
      );
      setApiTags(tagResult.source === 'api' ? tagResult.tags : []);
      setApiStations(stationResult.source === 'api' ? stationResult.stations : []);
      setApiSourceSystems(sourceSystemResult.source === 'api' ? sourceSystemResult.sourceSystems : []);
      setArchetypUniverse(
        archetypUniverseResult && archetypUniverseResult.source === 'api' ? archetypUniverseResult.archetypes : []
      );
      setArchetypCounts(
        archetypCountsResult && archetypCountsResult.source === 'api' ? archetypCountsResult.archetypes : []
      );
      // `null` bleibt stehen, wenn #783 die Aufschluesselung auf dieser
      // Instanz noch nicht liefert (source 'api', aber Feld fehlt) oder der
      // Abruf fehlschlug -- beides "unbekannt", nicht "0 je Status" (AK3c).
      setOmopStatusCounts(
        omopStatusCountsResult.source === 'api' ? omopStatusCountsResult.omopStatusCounts : null
      );
      setOpenehrStatusCounts(
        openehrStatusCountsResult.source === 'api' ? openehrStatusCountsResult.openehrStatusCounts : null
      );

      // Archetyp-Chips (#788, Review Runde 3, Blocker 1): der Facetten-Endpunkt
      // ist geraten und gegen kein Backend geprueft (siehe MR, Abschnitt
      // Annahmen). Schlaegt er fehl, waere die Chip-Zeile sonst ersatzlos und
      // still verschwunden (`ArchetypChips` zeigt bei leerer Liste nichts) --
      // das behandelt dieselbe Stelle wie einen Ausfall von Tags/Stationen/
      // Quellsystemen, statt einen eigenen, unsichtbaren Fehlerpfad zu
      // eroeffnen.
      const archetypFehlgeschlagen =
        zeigtOpenEhr(nameView)
        && ((archetypUniverseResult && archetypUniverseResult.source !== 'api')
          || (archetypCountsResult && archetypCountsResult.source !== 'api'));

      // Ein fehlendes Feld in der stats/-Antwort (#783 noch nicht ausgeliefert)
      // ist hier **kein** Fehlschlag -- `getCatalogStatusCounts` meldet das
      // ueber `null` in der Map, nicht ueber `source`. Nur ein tatsaechlicher
      // Abruffehler zaehlt.
      const statusCountsFehlgeschlagen =
        omopStatusCountsResult.source !== 'api' || openehrStatusCountsResult.source !== 'api';

      if (paramResult.source !== 'api') {
        setCatalogError(resolveCatalogStatusMessage(t, paramResult.error, tagResult.error, stationResult.error));
      } else if (
        tagResult.source !== 'api'
        || stationResult.source !== 'api'
        || sourceSystemResult.source !== 'api'
        || archetypFehlgeschlagen
        || statusCountsFehlgeschlagen
      ) {
        setCatalogError(
          tagResult.error?.status === 401
            || stationResult.error?.status === 401
            || sourceSystemResult.error?.status === 401
            || archetypUniverseResult?.error?.status === 401
            || archetypCountsResult?.error?.status === 401
            || omopStatusCountsResult?.error?.status === 401
            || openehrStatusCountsResult?.error?.status === 401
            ? t('dataCatalog.status.authRequired')
            : t('dataCatalog.status.filtersPartiallyAvailable')
        );
      }

      setCatalogLoading(false);
      // Erst hier ist der Vollsatz wirklich vollstaendig. Zwischen der ersten
      // Seite (`onPage`) und diesem Punkt liegt der Zustand, um den es geht.
      setCatalogNachladen(false);
    }
    loadCatalogData();
    return () => { cancelled = true; };
    // `t` bewusst nicht in den Abhaengigkeiten (#255, Runde 2): der Effekt laedt
    // Katalogdaten von der API, `t` uebersetzt nur die Fehlermeldung innerhalb.
    // Eine Uebersetzungsfunktion, deren Identitaet sich aendert (in Tests bei
    // jedem Render, siehe DataCatalog.test.js), darf keinen Netzwerkabruf
    // erneut ausloesen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogFilters, ordering, seite, seitengroesse, vollsatzModus, idsFilterModus, idsFilterKey, listeVergleichAktiv]);

  const catalogItems = catalogParameters;

  // Tag data
  const allTagsWithStats = apiTags.map(tag => ({ name: tag.value, count: tag.count || 0 }));
  const allTags = apiTags.map(t => t.value);
  const popularTags = apiTags.slice(0, 10).map(t => t.value);

  // Station data
  const allStations = apiStations.map(s => s.value);
  const allStationsWithCategories = allStations.map(station => ({
    name: station,
    category: 'Andere',
    building: '',
  }));
  const popularStations = apiStations.slice(0, 10).map(s => s.value);

  // Source systems — extracted from loaded parameters
  // Aus der API, nicht aus den geladenen Zeilen: mit Blaetterung waere das die
  // Auswahl der sichtbaren Seite, und ein Filterwert, den es gibt, waere
  // verschwunden -- ohne Fehlermeldung (#193).
  const allSourceSystems = apiSourceSystems.map(s => s.value);

  // parameterLists state is now managed by useWorkingLists hook
  // Use custom hook for filtering and sorting
  const {
    filteredParameters,
    sortedParameters,
    getParameterListStatus,
    getListComparisonCounts,
    getArchetypeCounts,
  } = useFilterSort({
    parameters: catalogItems,
    applyCatalogFiltersLocally: false,
    // Nur im Vollsatz-Modus darf der Browser sortieren -- sonst ordnet er die
    // sichtbaren 50 um und macht die Sortierung der API zunichte (#192).
    applySortLocally: vollsatzModus,
    searchTerm,
    selectedTags,
    selectedStations,
    selectedSensitivity,
    selectedOmopStatus,
    selectedSourceSystems,
    selectedArchetype,
    aiSuggestedParameterIds,
    setParameterIds,
    onlyUnvalidatedAiSuggestions,
    showOnlyActiveListParams,
    activeWorkingList,
    listComparisonFilter,
    orderBy,
    order,
    parameterLists,
    isParameterInActiveList,
  });

  // Archetyp-Chips (#788): der Vollbestand nennt jeden Archetyp, die Zaehlung
  // (unter den aktuell aktiven Filtern, ausser dem Archetyp-Chip selbst)
  // liefert die Anzahl dazu -- fehlt ein Archetyp dort, ist seine Anzahl
  // unter den aktiven Filtern 0 (AK5), nicht "gibt es nicht". "Ohne
  // Zuordnung" (`value: ''`) steht bewusst am Ende, unabhaengig von ihrer
  // Anzahl -- genau wie im Beispiel des Tickets.
  //
  // Die Zaehlung selbst kommt aus zwei verschiedenen Quellen (Review Runde 3,
  // Blocker 3): `catalogFilters` allein kennt die fuenf listenbasierten
  // Filter aus useFilterSort nicht (Arbeitsliste, Listenvergleich,
  // KI-Vorschlaege, Set, ungeprueft) -- ein `archetypCountsAbruf` ueber nur
  // `catalogFilters` naehme also z. B. "nur Parameter dieser Liste" nicht mit
  // und naeherte die Zaehlung an eine andere Menge als die Liste darunter
  // (AK3). Ist einer dieser fuenf Filter aktiv, zaehlt darum lokal aus
  // `getArchetypeCounts()`: `catalogItems` traegt in dem Fall garantiert die
  // vollstaendige gefilterte Menge (jeder der fuenf Filter erzwingt entweder
  // den Vollsatz- oder den ids-Filter-Modus, siehe `brauchtVollsatz`/
  // `idsFuerFilter` in katalogBlaetterung.js -- "beide Modi haben die volle
  // Menge im Speicher", siehe `blaettertLokal` oben). Ist keiner der fuenf
  // aktiv, bleibt der separate API-Abruf ueber `catalogFilters` korrekt --
  // und billiger als ein Vollsatz-Umweg nur fuer die Chips, auch wenn
  // `blaettertLokal` aus einem anderen Grund (Archetyp-Chip selbst, lokale
  // Sortierung) bereits true ist.
  const listenbasierteArchetypFilterAktiv =
    (aiSuggestedParameterIds && aiSuggestedParameterIds.length > 0) ||
    (setParameterIds && setParameterIds.length > 0) ||
    showOnlyActiveListParams ||
    onlyUnvalidatedAiSuggestions ||
    (listeVergleichAktiv && listComparisonFilter !== 'all');
  // Review Runde 4, NEU: `getArchetypeCounts()` liest `catalogItems`
  // (`catalogParameters`) -- im Vollsatz-Modus fuellt `onPage` diesen Zustand
  // seitenweise, bevor der Abruf tatsaechlich fertig ist (derselbe Zeitraum,
  // in dem `catalogNachladen` steht). Ohne diese Bedingung zaehlte die lokale
  // Zaehlung waehrend genau dieses Zeitraums ueber eine unvollstaendige
  // Menge -- derselbe Fehler, gegen den `gesamtzahl` (Zeile ~739) bereits mit
  // derselben `catalogDataSource === 'api'`-Abfrage abgesichert ist. Bis der
  // Abruf durch ist, bleibt es darum bei der weniger genauen, aber
  // vollstaendigen `archetypCounts`-Antwort (dem Stand vor Blocker 3) --
  // korrekt, sobald `catalogDataSource` auf `'api'` wechselt.
  const archetypChips = useMemo(() => {
    const zaehlung = listenbasierteArchetypFilterAktiv && catalogDataSource === 'api'
      ? getArchetypeCounts()
      : new Map(archetypCounts.map(a => [a.value || '', a.count || 0]));
    const sortiert = [...archetypUniverse].sort((a, b) => (a.value ? 0 : 1) - (b.value ? 0 : 1));
    return sortiert.map(({ value }) => ({
      value: value || '',
      count: zaehlung.get(value || '') || 0,
    }));
  }, [archetypUniverse, archetypCounts, listenbasierteArchetypFilterAktiv, catalogDataSource, getArchetypeCounts]);

  // Die angefragte Seite kann nach einem Filterwechsel hinter dem Ende liegen.
  // Dann nicht auf einer leeren Seite stehen bleiben, sondern auf die letzte.
  //
  // Der Listenvergleich (#277 Runde 2, B4) zaehlt hier ueber `filteredParameters`,
  // nicht `catalogCount`: geladen ist die Vereinigung beider Listen, ein
  // einzelner Chip ("nur A", "nur B", "beide") zeigt aber nur einen Teil davon
  // -- `catalogCount` bliebe die groessere Vereinigungszahl und liesse die
  // Seite auf einer leeren Seite stehen. Ausserhalb des Vollsatz-Modus laedt
  // `getParametersByIds` einmalig (kein `onPage`), die Zahl ist also stabil,
  // sobald sie da ist -- anders als im allgemeinen Vollsatz-Modus, wo
  // `filteredParameters.length` waehrend des Nachladens waechst und diese
  // Pruefung faelschlich zu frueh zurueckstufen wuerde (deshalb bleibt es dort
  // bei `catalogCount`, das im Vollsatz-Modus bewusst `null` bleibt).
  useEffect(() => {
    const massgeblicheZahl = (listeVergleichAktiv && !vollsatzModus) ? filteredParameters.length : catalogCount;
    if (massgeblicheZahl === null) return;
    const letzte = letzteSeite(massgeblicheZahl, seitengroesse);
    if (seite > letzte) setSeite(letzte);
  }, [catalogCount, filteredParameters.length, listeVergleichAktiv, vollsatzModus, seitengroesse, seite]);

  // Im Vollsatz- wie im ids-Filter-Modus (#277) blaettert der Browser: beide
  // haben die volle (gefilterte) Menge bereits im Speicher. Sonst hat die
  // Antwort schon genau eine Seite geliefert.
  const seitenParameter = blaettertLokal
    ? sortedParameters.slice((seite - 1) * seitengroesse, seite * seitengroesse)
    : sortedParameters;

  // Die Gesamtzahl: im Vollsatz-Modus die lokal gefilterte Menge, sonst `count`
  // aus der Antwort. `null` heisst unbekannt -- dann zeigt die Seite keine Zahl,
  // statt "0 Parameter" zu behaupten (#191).
  //
  // Im Vollsatz-Modus ist `catalogCount` immer `null` (Zeile ~322/371) -- die
  // lokal gefilterte Menge tritt an seine Stelle, aber **nur**, solange der
  // letzte Abruf tatsaechlich geglueckt ist. Ohne die `catalogDataSource`-Abfrage
  // ueberschreibt `filteredParameters.length` das bewusst auf `null` gesetzte
  // `catalogCount` schon waehrend des allerersten Ladens (leeres Array, Laenge
  // 0) und im Fehlerfall (Array wird auf [] zurueckgesetzt) -- genau die
  // Falschaussage "0 Parameter gefunden", gegen die #191 geschrieben wurde,
  // waere im Vollsatz-Modus weiterhin da. `catalogDataSource` selbst nimmt dafuer
  // nur noch die Werte, die ein Abruf liefert (`'idle'`, `'api'`, `'error'`) --
  // eine reine Anzeige-Praeferenz wie das Schliessen des Erfolgs-Hinweises darf
  // hier nicht mitlesen (Review Runde 2, Blocker 1; siehe `erfolgshinweisAusgeblendet`).
  //
  // Der Listenvergleich zaehlt hier wie der Vollsatz-Modus (#277 Runde 2, B4):
  // `catalogCount` traegt im ids-Filter-Modus nur die Groesse der geladenen
  // Vereinigung, nicht die des gerade gewaehlten Chips ("nur A", "nur B",
  // "beide") -- die steckt erst in `filteredParameters`, das
  // `matchesComparisonFilter` bereits anwendet.
  const gesamtzahl = (vollsatzModus || listeVergleichAktiv)
    ? (catalogDataSource === 'api' ? filteredParameters.length : catalogCount)
    : catalogCount;

  // Favoriten sind seit #255 keine lokale Liste mehr, sondern dieselbe
  // Arbeitsliste, die auch die Startseiten-Kachel zaehlt (#227) -- sonst zeigen
  // Stern und Kachel wieder zwei verschiedene Mengen.
  const favoritesList = resolveFavoritesList(parameterLists);

  // Enrich parameters with favorite status
  const parametersWithFavorites = seitenParameter.map(param => ({
    ...param,
    isFavorite: favoritesList ? isParameterInList(param.id, favoritesList.id) : false,
  }));

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // handleTagToggle is already provided by useDataCatalogState hook

  const handleFavoriteToggle = (parameterId, event) => {
    event?.stopPropagation(); // Prevent row click

    const parameter = catalogItems.find(p => p.id === parameterId);

    const applyToggle = (list) => {
      // `list.parameterIds` direkt, nicht `isParameterInList(parameterId,
      // list.id)` (#255, CI-Fund Job 1230/1364): letzteres sucht die Liste
      // erneut ueber die geschlossene `parameterLists`-Variable aus dem Render,
      // in dem der Klick ausgeloest wurde. Genau diese Verzoegerung war schon
      // einmal die Ursache eines Blockers in `useWorkingLists.handleAddToList`
      // (Runde 4) -- bei frisch angelegter Liste faellt sie hier nicht auf,
      // weil eine neue Liste ohnehin leer ist, aber bei einer bereits
      // bestehenden Liste waere sie derselbe Fehler unter anderem Namen. `list`
      // ist bereits die konkrete, aufgeloeste Liste (entweder der aktuelle
      // `favoritesList` oder die soeben von `createList()` zurueckgegebene) --
      // eine zweite, staerker verzoegerte Quelle fuer dieselbe Information zu
      // befragen kann nur falsch liegen, nie richtiger.
      const alreadyFavorite = list.parameterIds.includes(parameterId);
      const toggle = alreadyFavorite ? removeFromList : addToList;
      return toggle(parameterId, list.id, parameter?.name || '');
    };

    // Auf hospital1 liefert GET /api/olymp/lists/ heute [] -- ohne eigene
    // Favoriten-Liste wuerde jeder erste Klick nur den Fehler-Snackbar zeigen
    // (#255, Runde 2). Der erste Stern legt die private Liste also an, statt
    // auf eine Arbeitsliste zu verweisen, die es noch nicht gibt.
    const favoritesListPromise = favoritesList
      ? Promise.resolve(favoritesList)
      : creatingFavoritesListRef.current || (creatingFavoritesListRef.current = createList({
          name: FAVORITES_LIST_NAME,
          listType: 'generic',
          isPrivate: true,
        }).finally(() => {
          creatingFavoritesListRef.current = null;
        }));

    favoritesListPromise
      .then(applyToggle)
      .then((result) => {
        setSnackbar({
          open: true,
          message: result.message,
          severity: result.severity || (result.info ? 'info' : 'success')
        });
      })
      .catch(() => {
        setSnackbar({ open: true, message: t('dataCatalog.status.apiUnavailable'), severity: 'error' });
      });
  };

  // List Management Handlers
  // Immer den Dialog oeffnen -- kein Quick-Add mehr (#265). Bei genau einer
  // aktiven Arbeitsliste gab es hier bisher eine Abkuerzung, die direkt ohne
  // Dialog hinzufuegte. Das erzeugte zwei Bedienelemente je Zeile fuer dieselbe
  // Liste (dieses hier und die A/B-Spalte in ParameterRow), die sich noch dazu
  // unterschiedlich verhielten: die A/B-Spalte schaltet um (rein/raus), diese
  // Abkuerzung konnte nur hinzufuegen. Entscheidung (AK4): die A/B-Spalte
  // bleibt der Umschalter fuer die aktive Liste -- sie zeigt den Zustand auf
  // einen Blick beim Durchscrollen und ruft handleAddToList/handleRemoveFromList
  // direkt auf, unabhaengig davon, ob die Liste privat oder oeffentlich ist.
  // Dieser Knopf oeffnet ab jetzt immer den Dialog. **Nicht** deshalb, weil der
  // Dialog add/remove fuer alle Listen beherrscht -- das tut er nicht: er kann
  // nur aus privaten Listen entfernen, bei oeffentlichen Listen ausschliesslich
  // hinzufuegen (siehe Korrektur in listDialogHandlers.js). Der Grund ist, dass
  // dieser Knopf ohne aktive Liste ohnehin der einzige Weg in eine Liste war
  // und bleibt (AK3) -- und mit aktiver Liste die A/B-Spalte die Aenderung
  // schon vollstaendig abdeckt, ohne den Dialog zu brauchen.
  //
  // Die eigentliche Entscheidung steckt bewusst nicht hier, sondern in der
  // exportierten, eigens getesteten oeffneListenDialogFuerParameter -- ein
  // Test gegen diesen Wrapper haette die Quick-Add-Abkuerzung nicht rot werden
  // lassen koennen, weil er sie nur mockt statt sie auszufuehren. Dass eine
  // Abkuerzung nicht **hier**, direkt in diesem Handler, wieder einzieht --
  // an oeffneListenDialogFuerParameter vorbei --, deckt kein Unit-Test dieser
  // Datei ab, sondern erst der Seiten-Test in DataCatalog.test.js (Review
  // Runde 2, Blocker 1): er rendert den echten Katalog, klickt den echten
  // Knopf in der Aktionsspalte und prueft, dass dabei keine API zum
  // Hinzufuegen aufgerufen wird, bevor der Dialog offen ist.
  const handleOpenListDialog = (parameterId, event) => {
    event?.stopPropagation(); // Prevent row click

    oeffneListenDialogFuerParameter(parameterId, { setSelectedParameterForList, setListDialogOpen });
  };

  const handleRemoveFromList = (parameterId, listId, event) => {
    event?.stopPropagation();

    if (!parameterLists) return;

    const list = parameterLists.find(l => l.id === listId);
    const parameter = catalogItems.find(p => p.id === parameterId);

    if (list && parameter) {
      removeFromList(parameterId, listId, parameter.name).then((result) => {
        setSnackbar({
          open: true,
          message: result.message,
          severity: result.severity || (result.info ? 'info' : 'success')
        });
      });
    }
  };

  const handleAddToList = (parameterId, listId, event) => {
    event?.stopPropagation();

    if (!parameterLists) return;

    const list = parameterLists.find(l => l.id === listId);
    const parameter = catalogItems.find(p => p.id === parameterId);

    if (list && parameter) {
      addToList(parameterId, listId, parameter.name).then((result) => {
        setSnackbar({
          open: true,
          message: result.message,
          severity: result.severity || (result.info ? 'info' : 'success')
        });
      });
    }
  };

  // Working list functions are now provided by useWorkingLists hook
  // List comparison and filter functions are now provided by useFilterSort hook

  const handleConfirmListAdditionRequest = () => {
    const { list, parameter } = listAddRequestDialog;
    if (!list || !parameter) return;

    setListAddRequestDialog({ open: false, list: null, parameter: null });
  };

  useEffect(() => {
    if (!workingListsError) {
      return;
    }

    setSnackbar({
      open: true,
      message: resolveCatalogStatusMessage(t, workingListsError),
      severity: 'error'
    });
  }, [t, workingListsError, setSnackbar]);

  useEffect(() => {
    if (selectionLoading || selectionHydrated) {
      return;
    }

    setActiveWorkingList(workingListSelection.activeListIds);
    setShowOnlyActiveListParams(workingListSelection.showOnlyActiveListParams);
    setListComparisonFilter(workingListSelection.listComparisonFilter);
    setSelectionHydrated(true);
  }, [
    selectionHydrated,
    selectionLoading,
    setActiveWorkingList,
    setListComparisonFilter,
    setShowOnlyActiveListParams,
    workingListSelection,
  ]);

  useEffect(() => {
    if (!selectionHydrated) {
      return;
    }

    let cancelled = false;

    async function persistSelection() {
      try {
        const nextSelection = await persistWorkingListSelection({
          activeListIds: activeWorkingList,
          showOnlyActiveListParams,
          listComparisonFilter,
        });

        if (cancelled) {
          return;
        }

        if (JSON.stringify(nextSelection.activeListIds) !== JSON.stringify(activeWorkingList)) {
          setActiveWorkingList(nextSelection.activeListIds);
        }
        if (nextSelection.showOnlyActiveListParams !== showOnlyActiveListParams) {
          setShowOnlyActiveListParams(nextSelection.showOnlyActiveListParams);
        }
        if (nextSelection.listComparisonFilter !== listComparisonFilter) {
          setListComparisonFilter(nextSelection.listComparisonFilter);
        }
      } catch (persistError) {
        if (cancelled) {
          return;
        }

        setSnackbar({
          open: true,
          message: persistError.message || resolveCatalogStatusMessage(t, persistError),
          severity: 'error',
        });
      }
    }

    persistSelection();

    return () => {
      cancelled = true;
    };
  }, [
    activeWorkingList,
    listComparisonFilter,
    persistWorkingListSelection,
    selectionHydrated,
    setActiveWorkingList,
    setListComparisonFilter,
    setShowOnlyActiveListParams,
    setSnackbar,
    showOnlyActiveListParams,
    t,
  ]);

  useEffect(() => {
    setActiveWorkingList(prevActiveLists =>
      prevActiveLists.filter(listId => parameterLists.some(list => list.id === listId))
    );
  }, [parameterLists, setActiveWorkingList]);

  // handleRowClick and isRowExpanded are now provided by useDataCatalogState hook

  const handleAddTagToParameter = (parameterId, tagName, isPrivate = createTagPrivate, icon = null) => {
    // Check if tag exists
    const existingTag = allTagsWithStats.find(t => t.name === tagName);
    if (!existingTag) {
      // In real app: API call to create tag with privacy setting and icon
    }
    // Mock: This would update the parameter's tags in state
  };

  const handleRemoveTagFromParameter = (parameterId, tagName) => {
    const tag = allTagsWithStats.find(t => t.name === tagName);

    if (!tag) {
      return;
    }

    // If private tag and user is owner, allow direct deletion
    if (tag.isPrivate && tag.createdBy === currentUser) {
      return;
    }

    // If public tag, require approval process
    if (!tag.isPrivate) {
      setDeletionRequestDialog({ open: true, tag, parameterId });
      return;
    }

    // If trying to remove someone else's private tag (shouldn't happen in UI)
  };

  const handleCreateNewTag = (tagName, isPrivate = createTagPrivate) => {
    // In real app, this would call API to create tag
  };

  const handleMakeTagPublic = (tagName) => {
    const tag = allTagsWithStats.find(t => t.name === tagName);
    if (tag && tag.isPrivate && tag.createdBy === currentUser) {
    }
  };

  const handleRequestTagDeletion = (tagName, parameterId) => {
    // In real app: API call to add deletion request
    // If second user confirms, tag gets removed
  };

  const handleConfirmDeletionRequest = () => {
    const { tag, parameterId } = deletionRequestDialog;
    if (!tag) return;

    setDeletionRequestDialog({ open: false, tag: null });
  };

  const handleLoadSet = (set) => {
    // Load tags if present
    if (set.items.tags && set.items.tags.length > 0) {
      setSelectedTags(prev => {
        const newTags = set.items.tags.filter(tag => !prev.includes(tag));
        return [...prev, ...newTags];
      });
    }

    // Load stations if present
    if (set.items.stations && set.items.stations.length > 0) {
      setSelectedStations(prev => {
        const newStations = set.items.stations.filter(station => !prev.includes(station));
        return [...prev, ...newStations];
      });
    }

    // Load parameters if present - use setParameterIds for saved sets
    if (set.items.parameterIds && set.items.parameterIds.length > 0) {
      setSetParameterIds(prev => {
        const newIds = set.items.parameterIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }

    // Show success message
    setSnackbar({
      open: true,
      message: t('dataCatalog.status.setApplied', { name: set.name }),
      severity: 'success'
    });
  };

  const getSetTypeBadge = (type) => {
    switch (type) {
      case 'parameters': return 'PARAM';
      case 'stations': return 'STAT';
      case 'tags': return 'TAG';
      case 'mixed': return 'MIX';
      default: return 'SET';
    }
  };

  // AI Assistant Handlers
  //
  // Es gibt noch keine Mapping-/Vorschlagsschnittstelle, die aus einer
  // Studienbeschreibung Parameter ableitet (#273) -- weder im Backend noch als
  // dokumentierter Contract. Bisher stand hier eine Attrappe: zwei Sekunden
  // inszenierte Wartezeit, danach immer dieselben sechs Eintraege aus
  // `aiSuggestions.json` mit erfundenen Konfidenzwerten, unabhaengig vom
  // eingegebenen Text. Eine Ersatzliste zu erfinden waere derselbe Fehler in
  // neuer Form. Der Knopf ist deshalb deaktiviert (AK4, in
  // AIAssistantDialog); dieser Handler bleibt nur als Rueckfallebene, falls er
  // doch ausgeloest wird -- er zeigt ausschliesslich die Fehlermeldung, nie
  // eine Ersatzliste (AK1).
  const handleGenerateSuggestions = () => {
    setAiSuggestions([]);
    setSelectedSuggestions([]);
    setSnackbar({
      open: true,
      message: t('dataCatalog.status.aiSuggestionsUnavailable'),
      severity: 'error',
    });
  };

  const handleApplySuggestionsAsFilter = () => {
    // Only use selected suggestions, not all
    const parameterIds = selectedSuggestions;

    // Set as filter
    setAiSuggestedParameterIds(parameterIds);

    // Show success message
    setSnackbar({
      open: true,
      message: t('dataCatalog.messages.aiFiltersApplied', { count: parameterIds.length }),
      severity: 'success'
    });

    // Close dialog and reset
    setAiAssistantOpen(false);
    setStudyDescription('');
    setStudyType('');
    setSpecialty('');
    setAiSuggestions([]);
    setSelectedSuggestions([]);
  };

  // **Zweite Fassung derselben Regeln, geloescht (#246).** Diese lokalen Kopien
  // haben den Import aus `data_catalog/utils/helpers` beschattet: der Rueckfall
  // auf den lokalen Namen stand damit an *zwei* Stellen, und wer nur eine
  // reparierte, hatte den Fehler weiterhin im Produkt. Ab jetzt gilt eine
  // Fassung -- die importierte, mit `nameView` als Argument.
  const getPrimaryName = (param) => getPrimaryNameAusHelfer(param, nameView);
  const getSecondaryName = (param) => getSecondaryNameAusHelfer(param, nameView);

  // Die Beschreibung faellt bewusst weiter auf `param.description` zurueck: das
  // ist die **Herkunftszeile** ("Quelle: chartevents, Code 211"), keine Aussage
  // ueber ein OMOP-Konzept. Sie ist in jeder Ansicht richtig -- und fuer 576 von
  // 3900 Karten (14,8 %) das einzige, was zwei gleichnamige Zeilen unterscheidet
  // (#243). Ein `omopDescription` liefert die API heute nicht.
  const getPrimaryDescription = (param) => {
    if (nameView === 'omop') return param.omopDescription || param.description;
    return param.description;
  };

  // Utility function to get parameter name by ID
  const getParameterName = (paramId) => {
    const param = catalogItems.find(p => p.id === paramId);
    return param ? param.name : `Parameter ${paramId}`;
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>

        {/* API status banners */}
        {catalogLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="textSecondary">
              {t('dataCatalog.status.loading')}
            </Typography>
          </Box>
        )}
        {!catalogLoading && catalogError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCatalogError(null)}>
            {catalogError}
          </Alert>
        )}
        {!catalogLoading && catalogDataSource === 'api' && !erfolgshinweisAusgeblendet && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setErfolgshinweisAusgeblendet(true)}>
            {t('dataCatalog.status.loadedFromApi', { count: gesamtzahl })}
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 0 }}>
              {t('dataCatalog.title')}
            </Typography>
            <Tooltip title={t('dataCatalog.overviewTooltip')}>
              <IconButton
                size="small"
                color="primary"
                aria-label={t('dataCatalog.overviewAria')}
                onClick={() => setOverviewDialogOpen(true)}
                sx={{ mb: 0.5 }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => setMappingBatchDialogOpen(true)}
              sx={{ ml: 'auto' }}
            >
              {t('dataCatalog.startMappingBatch')}
            </Button>
          </Box>
          <Typography variant="body1" color="textSecondary">
            {gesamtzahl === null
              ? (catalogLoading ? t('dataCatalog.resultCountLoading') : t('dataCatalog.resultCountUnknown'))
              : t('dataCatalog.resultCount', { count: gesamtzahl })}
          </Typography>
        </Box>

        <DataCatalogHelpSection />

        {/* Compact Filter Bar with integrated search */}
        <CompactFilterBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          savedSets={savedSets}
          handleLoadSet={handleLoadSet}
          getSetTypeBadge={getSetTypeBadge}
          selectedTags={selectedTags}
          selectedStations={selectedStations}
          selectedSensitivity={selectedSensitivity}
          selectedOmopStatus={selectedOmopStatus}
          selectedOpenehrStatus={selectedOpenehrStatus}
          setSelectedOpenehrStatus={setSelectedOpenehrStatus}
          selectedSourceSystems={selectedSourceSystems}
          selectedArchetype={selectedArchetype}
          setSelectedArchetype={setSelectedArchetype}
          aiSuggestedParameterIds={aiSuggestedParameterIds}
          onlyUnvalidatedAiSuggestions={onlyUnvalidatedAiSuggestions}
          setParameterIds={setParameterIds}
          setSelectedTags={setSelectedTags}
          setSelectedStations={setSelectedStations}
          setSelectedSensitivity={setSelectedSensitivity}
          setSelectedOmopStatus={setSelectedOmopStatus}
          setSelectedSourceSystems={setSelectedSourceSystems}
          setAiSuggestedParameterIds={setAiSuggestedParameterIds}
          setOnlyUnvalidatedAiSuggestions={setOnlyUnvalidatedAiSuggestions}
          setSetParameterIds={setSetParameterIds}
          parameters={catalogItems}
          getParameterName={getParameterName}
          filterPanelOpen={filterPanelOpen}
          setFilterPanelOpen={setFilterPanelOpen}
          setAiAssistantOpen={setAiAssistantOpen}
          nameView={nameView}
          setNameView={setNameView}
          clearAllFilters={clearAllFilters}
        />

        {/* Filter Expansion Panel */}
        <FilterExpansionPanel
          open={filterPanelOpen}
          onClose={() => setFilterPanelOpen(false)}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          selectedStations={selectedStations}
          setSelectedStations={setSelectedStations}
          selectedSensitivity={selectedSensitivity}
          setSelectedSensitivity={setSelectedSensitivity}
          selectedOmopStatus={selectedOmopStatus}
          setSelectedOmopStatus={setSelectedOmopStatus}
          selectedOpenehrStatus={selectedOpenehrStatus}
          setSelectedOpenehrStatus={setSelectedOpenehrStatus}
          omopStatusCounts={omopStatusCounts}
          openehrStatusCounts={openehrStatusCounts}
          nameView={nameView}
          onlyUnvalidatedAiSuggestions={onlyUnvalidatedAiSuggestions}
          setOnlyUnvalidatedAiSuggestions={setOnlyUnvalidatedAiSuggestions}
          selectedSourceSystems={selectedSourceSystems}
          setSelectedSourceSystems={setSelectedSourceSystems}
          allSourceSystems={allSourceSystems}
          handleSourceSystemToggle={handleSourceSystemToggle}
          allTags={allTags}
          popularTags={popularTags}
          allStations={allStations}
          allStationsWithCategories={allStationsWithCategories}
          popularStations={popularStations}
          savedSets={savedSets}
          handleLoadSet={handleLoadSet}
          handleTagToggle={handleTagToggle}
          setTagDialogOpen={setTagDialogOpen}
        />

        {/* Active Working List Selector */}
        <Paper sx={{ p: 2, mb: 2, backgroundColor: activeWorkingList.length > 0 ? '#f0f7ff' : 'background.paper', border: activeWorkingList.length > 0 ? '2px solid' : 'none', borderColor: 'primary.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {t('dataCatalog.parameterListsTitle')}
            </Typography>
            <Tooltip
              title={t('dataCatalog.parameterListsTooltip')}
              arrow
            >
              <InfoOutlinedIcon fontSize="small" color="primary" sx={{ cursor: 'help' }} />
            </Tooltip>
          </Box>

          <Box
            data-testid="arbeitsliste-zeile"
            sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
          >
            <Box
              data-testid="arbeitsliste-label"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: istSchmalesFenster ? 0 : 180 }}
            >
              {activeWorkingList.length > 0 ? (
                <PlaylistAddCheckIcon color="primary" />
              ) : (
                <PlaylistAddIcon color="action" />
              )}
              <Typography variant="subtitle2" fontWeight="bold" color={activeWorkingList.length > 0 ? 'primary' : 'textSecondary'}>
                {activeWorkingList.length === 2
                  ? t('dataCatalog.activeWorkingListMany')
                  : t('dataCatalog.activeWorkingListOne')}
              </Typography>
            </Box>

            <FormControl size="small" sx={{ minWidth: 250, flexGrow: 1 }}>
              <Select
                multiple
                value={activeWorkingList}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limit to max 2 selections
                  if (Array.isArray(value) && value.length <= 2) {
                    setActiveWorkingList(value);
                  }
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return <em style={{ color: '#999' }}>{t('dataCatalog.noListSelected')}</em>;
                  }
                  const selectedLists = parameterLists ? selected.map(id => parameterLists.find(l => l.id === id)).filter(Boolean) : [];
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {selectedLists.map((list, idx) => (
                        <Box key={list.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: idx === 0 ? '#e3f2fd' : '#e8f5e9', px: 1, py: 0.5, borderRadius: 1 }}>
                          {selectedLists.length >= 1 && (
                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: idx === 0 ? '#2196f3' : '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.7rem', fontWeight: 'bold', mr: 0.5 }}>
                              {idx === 0 ? 'A' : 'B'}
                            </Box>
                          )}
                          {list.isPrivate ? <LockIcon fontSize="small" /> : <PublicIcon fontSize="small" />}
                          <Typography variant="body2" fontWeight="bold">{list.name}</Typography>
                          <Chip label={`${list.parameterIds.length}`} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                        </Box>
                      ))}
                    </Box>
                  );
                }}
              >
                <MenuItem value="" disabled sx={{ opacity: 1, fontStyle: 'italic', fontSize: '0.75rem', color: '#999', py: 1 }}>
                  {activeWorkingList.length === 0
                    ? t('dataCatalog.selectUpToTwo')
                    : activeWorkingList.length === 1
                      ? t('dataCatalog.selectSecondList')
                      : t('dataCatalog.maxReached')}
                </MenuItem>
                <MenuItem value="" disabled sx={{ opacity: 1, fontWeight: 'bold', fontSize: '0.75rem', color: '#666', py: 0.5 }}>
                  {`--- ${t('dataCatalog.privateLists')} ---`}
                </MenuItem>
                {parameterLists
                  .filter(list => list.isPrivate)
                  .map(list => (
                    <MenuItem
                      key={list.id}
                      value={list.id}
                      sx={{ pl: 3 }}
                      disabled={activeWorkingList.length >= 2 && !activeWorkingList.includes(list.id)}
                    >
                      <Checkbox
                        checked={activeWorkingList.includes(list.id)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      🔒 {list.name} {list.isDefault && '⭐'} ({list.parameterIds.length})
                    </MenuItem>
                  ))}
                <MenuItem value="" disabled sx={{ opacity: 1, fontWeight: 'bold', fontSize: '0.75rem', color: '#666', py: 0.5, mt: 1 }}>
                  {`--- ${t('dataCatalog.publicLists')} ---`}
                </MenuItem>
                {parameterLists
                  .filter(list => !list.isPrivate)
                  .map(list => (
                    <MenuItem
                      key={list.id}
                      value={list.id}
                      sx={{ pl: 3 }}
                      disabled={activeWorkingList.length >= 2 && !activeWorkingList.includes(list.id)}
                    >
                      <Checkbox
                        checked={activeWorkingList.includes(list.id)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      🌐 {list.name} ✓ ({list.parameterIds.length})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {activeWorkingList.length > 0 && (
              <Button
                size="small"
                startIcon={<CloseIcon />}
                onClick={() => {
                  setActiveWorkingList([]);
                  setShowOnlyActiveListParams(false);
                  setListComparisonFilter('all');
                }}
                variant="outlined"
              >
                {t('dataCatalog.deactivate')}
              </Button>
            )}

            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setListManagementDialogOpen(true)}
              variant="outlined"
            >
              {t('dataCatalog.manageAllLists')}
            </Button>

            {activeWorkingList.length === 1 && (
                <Chip
                  icon={showOnlyActiveListParams ? <CheckCircleIcon /> : <FilterListIcon />}
                  label={showOnlyActiveListParams ? t('dataCatalog.onlyActiveList') : t('dataCatalog.allParameters')}
                  onClick={() => setShowOnlyActiveListParams(!showOnlyActiveListParams)}
                  color={showOnlyActiveListParams ? "success" : "default"}
                variant={showOnlyActiveListParams ? "filled" : "outlined"}
                sx={{ ml: 'auto' }}
              />
            )}
          </Box>

          {/* Filter Chips for List Comparison */}
          {activeWorkingList.length === 2 && parameterLists && (() => {
            // Ausserhalb des Vollsatz-Modus laedt die Seite nur die Vereinigung
            // beider Listen -- "neither" muss dann aus der separat geladenen
            // Gesamtzahl folgen, nicht aus einer direkten Zaehlung (#277 Runde 2,
            // B4). Ist dieser Zusatzabruf (noch) nicht da, ist "neither"
            // unbekannt, nicht 0 (Runde 3, NEU-B6) -- `undefined` und `null`
            // sind hier bewusst zwei verschiedene Werte, siehe
            // `getListComparisonCounts`.
            const counts = getListComparisonCounts(vollsatzModus ? undefined : listComparisonTotalCount);
            const listA = parameterLists.find(l => l.id === activeWorkingList[0]);
            const listB = parameterLists.find(l => l.id === activeWorkingList[1]);

            return (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" fontWeight="bold" color="textSecondary">
                  {t('dataCatalog.comparison')}
                </Typography>
                <Chip
                  label={t('dataCatalog.listComparison.all', { count: filteredParameters.length })}
                  onClick={() => setListComparisonFilter('all')}
                  color={listComparisonFilter === 'all' ? 'primary' : 'default'}
                  variant={listComparisonFilter === 'all' ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  icon={<Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#2196f3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.7rem', fontWeight: 'bold' }}>A</Box>}
                  label={t('dataCatalog.listComparison.onlyA', { list: listA?.name, count: counts?.list1Only || 0 })}
                  onClick={() => setListComparisonFilter('list1Only')}
                  color={listComparisonFilter === 'list1Only' ? 'primary' : 'default'}
                  variant={listComparisonFilter === 'list1Only' ? 'filled' : 'outlined'}
                  size="small"
                  sx={{
                    '& .MuiChip-icon': {
                      color: '#fff !important'
                    }
                  }}
                />
                <Chip
                  icon={<Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: '#4caf50', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.7rem', fontWeight: 'bold' }}>B</Box>}
                  label={t('dataCatalog.listComparison.onlyB', { list: listB?.name, count: counts?.list2Only || 0 })}
                  onClick={() => setListComparisonFilter('list2Only')}
                  color={listComparisonFilter === 'list2Only' ? 'primary' : 'default'}
                  variant={listComparisonFilter === 'list2Only' ? 'filled' : 'outlined'}
                  size="small"
                  sx={{
                    '& .MuiChip-icon': {
                      color: '#fff !important'
                    }
                  }}
                />
                <Chip
                  label={t('dataCatalog.listComparison.both', { count: counts?.both || 0 })}
                  onClick={() => setListComparisonFilter('both')}
                  color={listComparisonFilter === 'both' ? 'secondary' : 'default'}
                  variant={listComparisonFilter === 'both' ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  label={
                    counts && counts.neither === null
                      ? t('dataCatalog.listComparison.neitherUnknown')
                      : t('dataCatalog.listComparison.neither', { count: counts?.neither || 0 })
                  }
                  onClick={() => setListComparisonFilter('neither')}
                  color={listComparisonFilter === 'neither' ? 'default' : 'default'}
                  variant={listComparisonFilter === 'neither' ? 'filled' : 'outlined'}
                  size="small"
                />
              </Box>
            );
          })()}

          {activeWorkingList.length === 1 && parameterLists && (
            <Alert
              severity={parameterLists.find(l => l.id === activeWorkingList[0])?.isPrivate ? "info" : "warning"}
              sx={{ mt: 2 }}
              icon={<CheckCircleIcon />}
            >
              <Typography variant="body2">
                <strong>{t('dataCatalog.labels.listAEnabled')}</strong> {t('dataCatalog.messages.listAEnabledInfo')}{' '}
                {parameterLists.find(l => l.id === activeWorkingList[0])?.isPrivate
                  ? t('dataCatalog.messages.listAEnabledInfoDirect')
                  : t('dataCatalog.messages.listAEnabledInfoSuggest')
                }
              </Typography>
            </Alert>
          )}

          {activeWorkingList.length === 2 && (
            <Alert
              severity="success"
              sx={{ mt: 2 }}
              icon={<CheckCircleIcon />}
            >
              <Typography variant="body2">
                <strong>{t('dataCatalog.labels.listComparisonEnabled')}</strong> {t('dataCatalog.messages.listComparisonInfo')}
              </Typography>
            </Alert>
          )}
        </Paper>

        {/* Archetyp-Chips (#788): nur in den Stellungen openEHR und "alle" --
            in OMOP und lokal waeren sie Rauschen (AK4). */}
        {zeigtOpenEhr(nameView) && (
          <ArchetypChips
            archetypes={archetypChips}
            selectedArchetype={selectedArchetype}
            onToggle={handleArchetypeToggle}
          />
        )}

        {/* Results Table */}
        <DataCatalogTable
          parameters={parametersWithFavorites}
          laedt={catalogLoading}
          nachladen={catalogNachladen}
          skelettzeilen={Math.min(seitengroesse, 10)}
          leerText={catalogError ? t('dataCatalog.table.emptyBecauseError') : ''}
          nameView={nameView}
          expandedRows={expandedRows}
          onRowClick={handleRowClick}
          onFavoriteToggle={handleFavoriteToggle}
          handleOpenListDialog={handleOpenListDialog}
          orderBy={orderBy}
          order={order}
          handleRequestSort={handleRequestSort}
          activeWorkingList={activeWorkingList}
          parameterLists={parameterLists}
          getParameterListStatus={getParameterListStatus}
          isParameterInAnyList={isParameterInAnyList}
          handleRemoveFromList={handleRemoveFromList}
          handleAddToList={handleAddToList}
          allTagsWithStats={allTagsWithStats}
          getIconComponent={getIconComponent}
          getPrimaryDescription={getPrimaryDescription}
          subtlePulse={subtlePulse}
        />

        {/*
          Die Blaetterung. Im Regelfall eine Anfrage je Seite (#193); im
          Vollsatz-Modus blaettert der Browser durch die lokal gefilterte Menge.
          Beides sieht fuer den Nutzer gleich aus -- und in beiden Faellen ist die
          Gesamtzahl die der Menge, nicht die der Seite.
        */}
        <KatalogBlaetterung
          seite={seite}
          seitengroesse={seitengroesse}
          gesamtzahl={gesamtzahl}
          onSeite={setSeite}
          onSeitengroesse={setSeitengroesse}
        />

        {/* Tag Management Dialog */}
        <TagManagementDialog
          open={tagDialogOpen}
          onClose={() => setTagDialogOpen(false)}
          allTagsWithStats={allTagsWithStats}
          currentUser={currentUser}
          getIconComponent={getIconComponent}
          handleMakeTagPublic={handleMakeTagPublic}
        />

        {/* Set Management Dialog */}
        <SetManagementDialog
          open={setManagementDialogOpen}
          onClose={() => setSetManagementDialogOpen(false)}
          savedSets={savedSets}
          activeSetTab={activeSetTab}
          setActiveSetTab={setActiveSetTab}
          handleLoadSet={handleLoadSet}
          getSetTypeBadge={getSetTypeBadge}
        />

        {/* Tag Deletion Request Dialog */}
        <TagDeletionRequestDialog
          open={deletionRequestDialog.open}
          onClose={() => setDeletionRequestDialog({ open: false, tag: null })}
          tag={deletionRequestDialog.tag}
          onConfirm={handleConfirmDeletionRequest}
        />

        {/* Parameter List Dialog - Add parameter to lists */}
        <WorkingListDialog
          open={listDialogOpen}
          onClose={() => setListDialogOpen(false)}
          selectedParameterId={selectedParameterForList}
          selectedParameter={catalogItems.find(p => p.id === selectedParameterForList)}
          parameterLists={parameterLists}
          currentUser={currentUser}
          isParameterInList={isParameterInList}
          handleToggleParameterInList={handleToggleParameterInList}
          hasParameterPendingApproval={hasParameterPendingApproval}
          setSnackbar={setSnackbar}
        />

        {/* List Management Dialog - Manage all lists */}
        <ListManagementDialog
          open={listManagementDialogOpen}
          onClose={() => setListManagementDialogOpen(false)}
          parameterLists={parameterLists}
          activeWorkingList={activeWorkingList}
          setActiveWorkingList={setActiveWorkingList}
          setSnackbar={setSnackbar}
          onCreateList={createList}
          onRenameList={renameList}
          onDeleteList={deleteList}
          onDuplicateList={duplicateList}
        />

        {/* List Addition Request Confirmation Dialog */}
        <ListAddRequestDialog
          open={listAddRequestDialog.open}
          onClose={() => setListAddRequestDialog({ open: false, list: null, parameter: null })}
          list={listAddRequestDialog.list}
          parameterId={listAddRequestDialog.parameter}
          onConfirm={handleConfirmListAdditionRequest}
        />

        {/* AI Assistant Modal */}
        <AIAssistantDialog
          open={aiAssistantOpen}
          onClose={() => setAiAssistantOpen(false)}
          studyDescription={studyDescription}
          setStudyDescription={setStudyDescription}
          studyType={studyType}
          setStudyType={setStudyType}
          specialty={specialty}
          setSpecialty={setSpecialty}
          aiSuggestions={aiSuggestions}
          setAiSuggestions={setAiSuggestions}
          selectedSuggestions={selectedSuggestions}
          setSelectedSuggestions={setSelectedSuggestions}
          handleGenerateSuggestions={handleGenerateSuggestions}
          handleApplySuggestionsAsFilter={handleApplySuggestionsAsFilter}
          isGeneratingSuggestions={isGeneratingSuggestions}
        />

        {/* Toast Notification */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={(event, reason) => {
            // Ignore clickaway - only close on timeout or manual close
            if (reason === 'clickaway') {
              return;
            }
            setSnackbar({ ...snackbar, open: false });
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Dialog open={overviewDialogOpen} onClose={() => setOverviewDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('dataCatalog.overviewDialogTitle')}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t('dataCatalog.overviewDialogDescription')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t('dataCatalog.overviewDialogListATitle')}</strong> {t('dataCatalog.overviewDialogListADescription')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>{t('dataCatalog.overviewDialogListBTitle')}</strong> {t('dataCatalog.overviewDialogListBDescription')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('dataCatalog.overviewDialogWorkflow')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOverviewDialogOpen(false)}>
              {t('common.close', { defaultValue: 'Close' })}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Mapping-Batch-Start-Dialog (#96) -- nur gemountet, waehrend er
            offen ist: er nutzt `useNavigate` fuer den Verweis auf den
            Job-Monitor nach dem Start (AK4), anders als die uebrigen hier
            immer gemounteten Dialoge braucht er deshalb keinen dauerhaften
            Platz im Baum. */}
        {mappingBatchDialogOpen && (
          <OmopMappingBatchStartDialog
            open
            onClose={() => setMappingBatchDialogOpen(false)}
          />
        )}
      </Box>
    </MainLayout>
  );
};

export default DataCatalog;
